/**
 * Componente LeadsFilters - Layout Reorganizado com Filtro de Motivo de Descarte Dinâmico
 * 
 * Este componente renderiza uma barra de filtros para gerenciamento de leads.
 * Permite filtrar leads por data, situação, ID, nome, WhatsAppId e motivo de descarte.
 * O filtro de motivo de descarte se atualiza dinamicamente baseado nos leads filtrados.
 */
import { Box, Button, MenuItem, TextField, Tooltip } from "@mui/material";
import { useEffect, useState } from "react";
import DashboardFilters, { SPECIFIC_DATES_FILTER } from '../Dashboard/DashboardFilters';
import FilterAltSharpIcon from '@mui/icons-material/FilterAltSharp';
import CleaningServicesRoundedIcon from '@mui/icons-material/CleaningServicesRounded';
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';

const LeadsFilters = ({
  situacaoFilter,
  setSituacaoFilter,
  descarteFilter,
  setDescarteFilter,
  idLeadFilter,
  setIdLeadFilter,
  nomeFilter,
  setNomeFilter,
  whatsAppIdFilter,
  setWhatsAppIdFilter,
  handleClearFilters,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  dateFilter,
  setDateFilter,
  motivosDescarte,
  empreendimentoFilter,
  setEmpreendimentoFilter,
  empreendimentosDisponiveis,
  handleApplyFilters,
  situacoesDisponiveis = [],
  onYearChange,                // NOVA prop
  onExportLeads,
  exportDisabled = false,
}) => {
  const [selectedYear, setSelectedYear] = useState('');

  const currentYear = new Date().getFullYear();
  const years = [];
  for (let year = 2023; year <= currentYear; year++) {
    years.push(year);
  }

  const handleDateFilterChange = (value) => {
    setDateFilter(value);

    if (value === 'Anual') {
      // Ao selecionar "Anos", limpa datas e ano
      setStartDate('');
      setEndDate('');
      setSelectedYear('');
    } else if (value !== SPECIFIC_DATES_FILTER) {
      // Outros filtros automáticos: limpa datas e ano
      setStartDate('');
      setEndDate('');
      setSelectedYear('');
    }
  };


  useEffect(() => {
    // Limpar filtro de descarte quando situação não for "Descartado" 
    // ou quando não há motivos disponíveis
    if (situacaoFilter !== "Descartado" || motivosDescarte.length === 0) {
      if (descarteFilter) {
        setDescarteFilter('');
      }
    }
  }, [situacaoFilter, motivosDescarte.length, descarteFilter, setDescarteFilter]);

  const fieldBaseSx = {
    width: { xs: '100%', md: 135.93 },
    bgcolor: 'background.paper',
    '& .MuiInputBase-input': {
      color: 'text.primary',
    },
    '& .MuiInputLabel-root': {
      color: 'text.secondary',
    },
    '& .MuiOutlinedInput-root': {
      borderRadius: 1.5,
      '& fieldset': {
        borderColor: 'divider',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#667eea',
      },
      '&.Mui-disabled': {
        backgroundColor: (theme) =>
          theme.palette.mode === 'dark'
            ? 'rgba(255,255,255,0.05)'
            : 'rgba(0,0,0,0.05)',
      },
      '&.Mui-disabled .MuiInputBase-input': {
        WebkitTextFillColor: (theme) =>
          theme.palette.mode === 'dark'
            ? 'rgba(255,255,255,0.5)'
            : 'rgba(0,0,0,0.38)',
      },
    },
    '& .MuiSelect-icon': {
      color: 'text.secondary',
    },
  };

  const actionButtonBaseSx = {
    minWidth: 0,
    width: { xs: '100%', md: 35 },
    height: { xs: 40, md: 35 },
    borderRadius: { xs: 1.5, md: '50%' },
    padding: { xs: '0 12px', sm: 0 },
    textTransform: 'none',
    fontWeight: 600,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: { xs: 1, sm: 0 },
  };

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        borderRadius: { xs: 2, md: 3 },
        p: { xs: 2, md: 3 },
        mb: 2,
        boxShadow: {
          xs: '0 2px 10px rgba(0,0,0,0.08)',
          md: '0 4px 20px rgba(0,0,0,0.08)'
        },
        //mx: 'auto',
        maxWidth: '100%',
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      {/* Primeira linha - Filtros principais (sempre visíveis) */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: 1.5, md: 2 },
          mb: 2,
          alignItems: { xs: 'stretch', md: 'center' },
          justifyContent: { xs: 'stretch', md: 'center' },
        }}
      >
        {/* Filtro por Período */}
        <DashboardFilters
          dateFilter={dateFilter}
          onChange={handleDateFilterChange}
          marginB={0}
          sx={{ width: { xs: '100%', md: 135.93 } }}
        />

        {dateFilter === 'Anual' && (
          <TextField
            select
            label="Ano"
            value={selectedYear}
            onChange={(e) => {
              const year = e.target.value;
              setSelectedYear(year);
              if (onYearChange) {
                onYearChange(year); // Atualiza datas globais e dispara requisição
              }
            }}
            variant="outlined"
            size="small"
            sx={{
              ...fieldBaseSx,
            }}
          >
            {years.map((year) => (
              <MenuItem key={year} value={year}>
                {year}
              </MenuItem>
            ))}
          </TextField>
        )}

        {/* Data de Início */}
        <TextField
          type="date"
          label="Data de Início"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          disabled={dateFilter !== SPECIFIC_DATES_FILTER}
          variant="outlined"
          size="small"
          error={startDate && endDate && startDate > endDate}
          helperText={startDate && endDate && startDate > endDate ? "Data inválida" : ""}
          inputProps={{
            max: endDate || undefined
          }}
          InputLabelProps={{
            shrink: true,
          }}
          sx={{
            ...fieldBaseSx,
          }}
        />

        {/* Data de Fim */}
        <TextField
          type="date"
          label="Data de Fim"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          disabled={dateFilter !== SPECIFIC_DATES_FILTER}
          variant="outlined"
          size="small"
          error={startDate && endDate && endDate < startDate}
          helperText={startDate && endDate && endDate < startDate ? "Data inválida" : ""}
          inputProps={{
            min: startDate || undefined
          }}
          InputLabelProps={{
            shrink: true,
          }}
          sx={{
            ...fieldBaseSx,
          }}
        />

        {/* Filtro de situação */}
        <TextField
          select
          value={situacaoFilter}
          onChange={(e) => setSituacaoFilter(e.target.value)}
          variant="outlined"
          label="Situação"
          size="small"
          sx={{
            ...fieldBaseSx,
          }}
        >
          {/* Sempre manter a opção "Todos" */}
          <MenuItem value="Todos">Todos</MenuItem>

          {/* Opções dinâmicas vindas das situações internas */}
          {situacoesDisponiveis.map((situacao) => (
            <MenuItem key={situacao} value={situacao}>
              {situacao}
            </MenuItem>
          ))}
        </TextField>


        {/* Filtro de Motivo de Descarte - Visível apenas se situação for "Descartado" */}
        {situacaoFilter === "Descartado" && (
          <TextField
            select
            value={descarteFilter}
            onChange={(e) => setDescarteFilter(e.target.value)}
            variant="outlined"
            label="Motivo de Descarte"
            size="small"
            sx={{
              ...fieldBaseSx,
              '& .MuiInputLabel-root': {
                color: motivosDescarte.length === 0 ? 'rgba(0,0,0,0.38)' : 'inherit',
              },
            }}
          >
            <MenuItem value="">
              <em>
                {motivosDescarte.length === 0
                  ? 'Nenhum motivo disponível'
                  : 'Todos os motivos'}
              </em>
            </MenuItem>
            {motivosDescarte.map((motivo, index) => (
              <MenuItem key={`descarte-${index}`} value={motivo}>
                {motivo}
              </MenuItem>
            ))}
          </TextField>
        )}

        {/* Campo ID-Lead */}
        <TextField
          label="ID Lead"
          value={idLeadFilter}
          onChange={(e) => {
            // Permite apenas números
            const value = e.target.value.replace(/\D/g, '');
            setIdLeadFilter(value);
          }}
          variant="outlined"
          size="small"
          placeholder="Ex: 12345"
          InputLabelProps={{
            shrink: idLeadFilter ? true : false
          }}
          sx={{
            ...fieldBaseSx,
          }}
        />

        {/* Campo Nome */}
        <TextField
          label="Nome"
          value={nomeFilter}
          onChange={(e) => setNomeFilter(e.target.value)}
          variant="outlined"
          size="small"
          placeholder="Digite o nome"
          InputLabelProps={{
            shrink: nomeFilter ? true : false
          }}
          sx={{
            ...fieldBaseSx,
          }}
        />

        {/* Campo WhatsApp ID */}
        <TextField
          label="WhatsApp ID"
          value={whatsAppIdFilter}
          onChange={(e) => setWhatsAppIdFilter(e.target.value)}
          variant="outlined"
          size="small"
          placeholder="Digite o WhatsApp ID"
          InputLabelProps={{
            shrink: whatsAppIdFilter ? true : false
          }}
          sx={{
            ...fieldBaseSx,
          }}
        />

        {/*Botão de Aplicar Filtros*/}
        <Tooltip title="Aplicar Filtros" arrow>
          <Button
            variant="contained"
            onClick={handleApplyFilters}
            sx={{
              ...actionButtonBaseSx,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                boxShadow: '0 6px 20px rgba(102, 126, 234, 0.3)',
                transform: 'translateY(-1px)',
              },
            }}
          >
            <FilterAltSharpIcon />
            <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}>
              Aplicar filtros
            </Box>
          </Button>
        </Tooltip>

        {/*Botão de Limpar Filtros*/}
        <Tooltip title="Limpar Filtros" arrow>
          <Button
            variant="contained"
            onClick={handleClearFilters}
            sx={{
              ...actionButtonBaseSx,
              background: (theme) =>
                theme.palette.mode === 'dark'
                  ? 'rgba(42, 133, 226, 0.14)'
                  : 'rgba(102, 126, 234, 0.08)',
              color: 'primary.main',
              border: '1px solid',
              borderColor: 'primary.main',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                background: (theme) =>
                  theme.palette.mode === 'dark'
                    ? 'rgba(42, 133, 226, 0.24)'
                    : 'rgba(102, 126, 234, 0.14)',
                boxShadow: '0 6px 20px rgba(102, 126, 234, 0.2)',
                transform: 'translateY(-1px)',
              },
            }}
          >
            <CleaningServicesRoundedIcon />
            <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}>
              Limpar filtros
            </Box>
          </Button>
        </Tooltip>

        {typeof onExportLeads === 'function' && (
          <Tooltip title="Exportar Planilha" arrow>
            <Button
              variant="contained"
              onClick={onExportLeads}
              disabled={exportDisabled}
              sx={{
                ...actionButtonBaseSx,
                background: 'linear-gradient(135deg, #43a047 0%, #2e7d32 100%)',
                color: 'white',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #388e3c 0%, #1b5e20 100%)',
                  boxShadow: '0 6px 20px rgba(67, 160, 71, 0.3)',
                  transform: 'translateY(-1px)',
                },
                '&:disabled': {
                  opacity: 0.5,
                  cursor: 'not-allowed',
                  boxShadow: 'none',
                  transform: 'none',
                },
              }}
            >
              <FileDownloadRoundedIcon />
              <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}>
                Exportar
              </Box>
            </Button>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

export default LeadsFilters;
